// src/components/Tests/SidebarPalette.tsx
import React from "react";
import "./SidebarPalette.css";

interface Question {
  id:   string;
  [key: string]: any;
}

interface SectionData {
  section_name: string;
  questions:    Question[];
}

interface QStatus {
  answered:          boolean;
  marked_for_review: boolean;
  visited:           boolean;
}

interface Props {
  sections:       SectionData[];
  questionStatus: { [qid: string]: QStatus };
  onPaletteClick: (globalIdx: number) => void;
  currentQIdx:    number;
  isMulti?:       boolean;
}

const SidebarPalette: React.FC<Props> = ({
  sections,
  questionStatus,
  onPaletteClick,
  currentQIdx,
  isMulti = false,
}) => {
  /*
   * KEY FIX — compute cumulative offsets from actual array lengths,
   * never from a DB field like no_of_questions which may be missing/wrong.
   *
   * sectionOffsets[i] = total questions in sections 0..i-1
   * e.g. Reasoning=20q → offset=0, Quant=20q → offset=20
   */
  const sectionOffsets: number[] = [];
  let cumulative = 0;
  for (const sec of sections) {
    sectionOffsets.push(cumulative);
    cumulative += sec.questions.length;
  }

  return (
    <div className={`sp-wrap${isMulti ? " sp-multi" : ""}`}>
      {sections.map((sec, sIdx) => {
        const sectionOffset = sectionOffsets[sIdx]; // e.g. 0 for Reasoning, 20 for Quant

        return (
          <div key={`section-${sIdx}`} className="sp-section">

            {/* Section header */}
            <div className="sp-section-hdr">
              <span className="sp-sec-name">{sec.section_name}</span>
              <span className="sp-sec-count">{sec.questions.length}Q</span>
            </div>

            {/* Question buttons */}
            <div className="sp-grid">
              {sec.questions.map((q, localIdx) => {
                /*
                 * globalIdx is UNIQUE across all sections:
                 *   Reasoning Q1  → sectionOffset=0  + localIdx=0 = 0
                 *   Reasoning Q20 → sectionOffset=0  + localIdx=19 = 19
                 *   Quant Q1      → sectionOffset=20 + localIdx=0  = 20   ← never clashes with 0
                 *   Quant Q2      → sectionOffset=20 + localIdx=1  = 21
                 */
                const globalIdx = sectionOffset + localIdx;
                const status    = questionStatus[q.id] || {};
                const isCurrent = currentQIdx === globalIdx; // only ONE button is true

                let cls = "sp-btn";
                if (status.marked_for_review && status.answered) cls += " sp-rev-ans";
                else if (status.marked_for_review)               cls += " sp-rev";
                else if (status.answered)                        cls += " sp-ans";
                else if (status.visited)                         cls += " sp-vis";
                if (isCurrent)                                   cls += " sp-cur";

                return (
                  <button
                    key={`${sIdx}-${q.id}`}    // unique key even if q.id is same across sections
                    className={cls}
                    onClick={() => onPaletteClick(globalIdx)}
                    title={`Q${globalIdx + 1} — ${sec.section_name}`}
                  >
                    {globalIdx + 1}
                    {status.marked_for_review && status.answered && (
                      <span className="sp-tick">✓</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SidebarPalette;